/**
 * WalkthroughTooltip.js
 *
 * Custom Joyride tooltip component.
 * Design principles:
 *  - No emoji, no celebratory language
 *  - Subtle entrance animation (no spring overshoot)
 *  - Full light/dark theme support
 *  - Professional button hierarchy: text secondary "Back", solid primary "Continue"
 *  - Progress shown as a thin top bar + muted step count only
 */

import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

const WalkthroughTooltip = ({
  backProps,
  closeProps,
  controls,
  continuous,
  index,
  isLastStep,
  size,
  step,
}) => {
  const { isDark } = useTheme();
  const progressPct = Math.round(((index + 1) / size) * 100);

  // ── Token set ─────────────────────────────────────────────────────────────
  const bg         = isDark ? '#0f1729' : '#ffffff';
  const border     = isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)';
  const shadow     = isDark
    ? '0 16px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)'
    : '0 8px 24px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)';
  const textPrimary  = isDark ? '#e8edf5' : '#1a2035';
  const textSecondary = isDark ? 'rgba(232,237,245,0.58)' : '#64748b';
  const divider      = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
  const progressBg   = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const backBg       = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const backBorder   = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const backColor    = isDark ? 'rgba(232,237,245,0.65)' : '#475569';
  const dotInactive  = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
  const dotPast      = isDark ? 'rgba(248,114,23,0.4)' : 'rgba(248,114,23,0.35)';
  const accentColor  = '#F87217';
  const skipColor    = isDark ? 'rgba(232,237,245,0.28)' : 'rgba(0,0,0,0.28)';

  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 14,
        boxShadow: shadow,
        maxWidth: 368,
        minWidth: 300,
        padding: 0,
        overflow: 'hidden',
        // Subtle fade + lift — no spring overshoot
        animation: 'wtIn 0.2s ease-out',
        fontFamily: "'Inter', system-ui, sans-serif",
        willChange: 'transform, opacity',
      }}
      role="dialog"
      aria-label={step.title}
    >
      {/* ── Progress bar ── */}
      <div style={{ height: 2, background: progressBg }}>
        <div
          style={{
            height: '100%',
            width: `${progressPct}%`,
            background: accentColor,
            transition: 'width 0.35s cubic-bezier(0.4,0,0.2,1)',
          }}
        />
      </div>

      {/* ── Body ── */}
      <div style={{ padding: '18px 20px 14px' }}>
        {/* Step counter + Skip */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: accentColor,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            {index + 1} / {size}
          </span>

          <button
            onClick={controls.skip}
            style={{
              background: 'transparent',
              border: 'none',
              color: skipColor,
              fontSize: 11,
              cursor: 'pointer',
              padding: '2px 0',
              fontFamily: 'inherit',
              transition: 'color 0.15s ease',
              lineHeight: 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = isDark ? 'rgba(232,237,245,0.6)' : 'rgba(0,0,0,0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = skipColor;
            }}
            aria-label="Skip tour"
          >
            Skip
          </button>
        </div>

        {/* Title */}
        {step.title && (
          <h3
            style={{
              color: textPrimary,
              fontSize: 14,
              fontWeight: 600,
              margin: '0 0 7px',
              letterSpacing: '-0.01em',
              lineHeight: 1.35,
            }}
          >
            {step.title}
          </h3>
        )}

        {/* Content */}
        <p
          style={{
            color: textSecondary,
            fontSize: 13,
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          {step.content}
        </p>
      </div>

      {/* ── Footer ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 20px 16px',
          borderTop: `1px solid ${divider}`,
          gap: 8,
        }}
      >
        {/* Step dots */}
        <div style={{ display: 'flex', gap: 4, flex: 1, alignItems: 'center' }}>
          {Array.from({ length: size }).map((_, i) => (
            <div
              key={i}
              style={{
                width: i === index ? 16 : 5,
                height: 5,
                borderRadius: 3,
                background:
                  i === index ? accentColor : i < index ? dotPast : dotInactive,
                transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1), background 0.2s ease',
                flexShrink: 0,
              }}
            />
          ))}
        </div>

        {/* Navigation buttons */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {index > 0 && (
            <button
              {...backProps}
              style={{
                padding: '6px 14px',
                borderRadius: 8,
                background: backBg,
                border: `1px solid ${backBorder}`,
                color: backColor,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'background 0.15s ease, color 0.15s ease',
                lineHeight: 1.4,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDark
                  ? 'rgba(255,255,255,0.09)'
                  : 'rgba(0,0,0,0.07)';
                e.currentTarget.style.color = isDark ? '#e8edf5' : '#1a2035';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = backBg;
                e.currentTarget.style.color = backColor;
              }}
              aria-label="Previous step"
            >
              Back
            </button>
          )}

          {continuous ? (
            <button
              onClick={controls.next}
              style={{
                padding: '6px 16px',
                borderRadius: 8,
                background: accentColor,
                border: 'none',
                color: '#fff',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'opacity 0.15s ease, transform 0.15s ease',
                lineHeight: 1.4,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.88';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
              aria-label={isLastStep ? 'Finish tour' : 'Next step'}
            >
              {isLastStep ? 'Finish' : 'Continue'}
            </button>
          ) : (
            <button
              {...closeProps}
              style={{
                padding: '6px 16px',
                borderRadius: 8,
                background: accentColor,
                border: 'none',
                color: '#fff',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                lineHeight: 1.4,
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes wtIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default WalkthroughTooltip;
